// src/App.tsx
import React, {ReactNode, useEffect, useMemo, useRef, useState} from 'react'
import { Routes, Route, Navigate, Outlet } from 'react-router-dom'
import { ToastProvider } from '../contextManager/toast.context'
import { UserProvider, useUser } from '../contextManager/user.context';
import { AxiosInstanceProvider } from '../contextManager/axios.context'
import { TooltipProvider } from '@radix-ui/react-tooltip'
import { HelmetProvider } from 'react-helmet-async'
import  {QueryClientProvider } from '@tanstack/react-query'
import {queryClient} from '../contextManager/queryClient'
import {SimpleHeader} from '../components/navigation/SimpleHeader'
import { ScaleProvider, useScale } from './HeightScaler';
import { AuthProtectedRoute } from '../components/guards/AuthProtectedRoute'

import { globalStyles } from '../styles/stitches.config'
import { cateModeGlobalCSS } from '../styles/theming/cateMode'

import { Area, Viewport as ScrollViewport, Scrollbar, Thumb } from '../styles/_app.style'

import Login from './Login2'
import Register from './Register'
import InvalidRequest404 from './InvalidRequest404'
import { TimelineDateProvider } from '../contextManager/timelineDate.context'
import ProfilePage from './Profile'
import Timeline from './strippedTimeline'
import RegisterChoice from './RegisterChoice'
import ParentRegister from './ParentRegister'
import ParentProfilePage from './ParentProfile';
import SearchPage from './SearchPage';
import MessengerPage from './MessengerPage';
import TutorSearchPage from './TutorSearchPage';
import LessonPage from './LessonPage';
import VideoRoom from './VideoRoom';
import { NAVIGATION_HEIGHT } from '../constants/global';
import { OfflineIndicator } from '../components/OfflineIndicator';
import VerifyEmail from './VerifyEmail';
import ForgotPassword from './ForgotPassword';
import ResetPassword from './ResetPassword';
import LandingPage from './LandingPage';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfService from './TermsOfService';
import AdminReviewPage from './AdminReview';



const RoleBasedProtectedRoute = ( {allowedRoles} : { allowedRoles: string[] }) => {
  const { userDetails } = useUser();

  if (!userDetails) {
    return <Navigate to="/login" replace />;
  }


  return allowedRoles.includes(userDetails.role) ? (
    <Outlet />
  ) : (
    <Navigate to="/login" replace />
  );
};

type RoleRoute = {
  role: string;
  element: JSX.Element;
};

// 2. Define the shape of the props for the RoleSwitch component
type RoleSwitchProps = {
  routes: RoleRoute[];
  fallback?: JSX.Element; // <-- FIXED: Added the optional fallback property to the type definition.

};

// 3. Apply the type to the component's props
const RoleSwitch = ({ routes, fallback = <Navigate to="/not-found" replace /> }: RoleSwitchProps) => {
  const { userDetails } = useUser();
  if (!userDetails) return null;
  
  // TypeScript now knows 'route' is of type RoleRoute, fixing the second error
  const routeForRole = routes.find(route => route.role === userDetails.role);

  return routeForRole ? routeForRole.element : fallback;
};



function App() {
  globalStyles()
  cateModeGlobalCSS()

  const mainScrollContainerRef = useRef<HTMLDivElement | null>(null);


  return (
    <HelmetProvider>
      <ToastProvider>
        <QueryClientProvider client={queryClient}>
        <UserProvider>
          <TooltipProvider>
            <OfflineIndicator/>
            <AxiosInstanceProvider>
            <ScaleProvider> {/* <-- USE THE CORRECTED COMPONENT */}
            <Area >
                    <ScrollViewport ref={mainScrollContainerRef}>
                      <Routes>
                      <Route
                        index
                        element={<><SimpleHeader showAuth /><LandingPage /></>}
                      />
                      <Route
                        path="login"
                        element={
                          <>
                            <SimpleHeader />
                            <Login />
                          </>
                        }
                      />
                      <Route
                        path="register"
                        element={
                          <>
                            <SimpleHeader />
                            {/* <Register /> */}
                            <RegisterChoice />
                          </>
                        }
                      />
                      <Route
                        path="register/tutor"
                        element={
                          <>
                            <SimpleHeader/>
                            <Register/>
                          </>
                        }/>

                        <Route
                          path="register/parent"
                          element={
                            <>
                              <SimpleHeader/>
                              <ParentRegister/>
                            </>
                          }/>

                        <Route
                          path="verify-email"
                          element={
                            <>
                              <SimpleHeader />
                              <VerifyEmail />
                            </>
                          }
                        />
                        <Route path="forgot-password" element={<><SimpleHeader /><ForgotPassword /></>} />
                        <Route path="reset-password" element={<><SimpleHeader /><ResetPassword /></>} />
                        <Route
                        element={
                          <TimelineDateProvider>
                            <AuthProtectedRoute />
                          </TimelineDateProvider>
                        }
                      >
                        <Route
                          path="profile"
                          element={
                            <RoleSwitch
                              routes={[
                                // Definition 1: Kept separate
                                {role: 'tutor', element: <ProfilePage /> },
                                {role: 'parent', element: <ParentProfilePage/>}
                              ]}
                            />
                          }
                        />



                      <Route
                          path="search"
                          element={
                            <RoleSwitch
                              routes={[
                                {role: 'parent', element: <SearchPage scrollContainerRef={mainScrollContainerRef}/>},
                                {role: 'tutor', element: <TutorSearchPage scrollContainerRef={mainScrollContainerRef}/>}
                              ]}
                            />
                          }
                        />

                  <Route
                    path="messenger"
                    element={
                      <RoleSwitch
                        routes={[
                          // Both roles can access the messenger
                          {role: 'parent', element: <MessengerPage />},
                          {role: 'tutor', element: <MessengerPage />}
                        ]}
                      />
                    }
                  />

                  <Route
                    path="lessons"
                    element={
                      <RoleSwitch
                        routes={[
                          {role: 'parent', element: <LessonPage />},
                          {role: 'tutor', element: <LessonPage />}
                        ]}
                        />
                    }
                    />



                        {/* Guarded inside the page: renders 404 unless userDetails.isAdmin */}
                        <Route path="admin" element={<AdminReviewPage />} />

                        <Route path="room/:eventId" element={<VideoRoom />} />

                        <Route path="strippedTimeline" element={<Timeline />} />

                        </Route>
                        <Route path="privacy" element={<><SimpleHeader showAuth /><PrivacyPolicy /></>} />
                        <Route path="terms" element={<><SimpleHeader showAuth /><TermsOfService /></>} />
                        <Route path="*" element={<InvalidRequest404 />} />
                      </Routes>
                    </ScrollViewport>
                    <Scrollbar orientation="vertical">
                      <Thumb />
                    </Scrollbar>
                  </Area>
                </ScaleProvider>
            </AxiosInstanceProvider>
          </TooltipProvider>
        </UserProvider>
        </QueryClientProvider>
      </ToastProvider>
    </HelmetProvider>
  )
}

export default App
